/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import Highlighter from 'react-highlight-words';

// Component imports
import { Button, Col, Collapse, Grid, Input, Modal, Popconfirm, Row, Space, Spin, Table, Tooltip, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import NlpFlowEditor from '../UI/NlpFlowEditor';
import Permissioned from '../Permissioned';
import NlpPipelineLink from '../UI/NlpPipelineLink';
import NlpFlowForm from '../Forms/NlpFlowForm';
import { EditIcon } from '../Icons/EditIcon';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';

// Misc imports
import { LANGUAGE_OPTIONS, PERMISSIONS_GROUP_ADMIN, DEFAULT_DATETIME_FORMAT, DEFAULT_PAGINATION_SIZE } from '../../constants/main';
import { PERMISSIONS_GROUP_EDITOR, PERMISSIONS_GROUP_VIEWER } from '../../constants/main';
import { useCreateNlpFlowMutation, useDeleteNlpFlowMutation, useFetchNlpFlowsQuery } from '../../store/nlpFlows/nlpFlowsApi';
import { useFetchNlpPipelinesQuery } from '../../store/nlpPipelines/nlpPipelinesApi';
import { NODE_TYPES } from '../../constants/flowGraph';
import { useDragDetection } from '../../utils/hooks/useDragDetection';
import useUserPermissions from '../../utils/hooks/useUserPermissions';

// Constant declarations
const { Text } = Typography;
const { useBreakpoint } = Grid;
export const PANEL_KEYS = {
  LIST: 'list',
  EDIT: 'edit'
};

const NlpFlowsTable = () => {
  const { tenantId, orgId } = useParams();
  const { t } = useTranslation();
  const userPermissions = useUserPermissions(orgId, tenantId);
  const screens = useBreakpoint();
  const isMobileSize = screens.xs;
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const { handleMouseDown, isDragging } = useDragDetection();

  // Store state
  const { data: nlpFlows, isLoading: isLoadingNlpFlows } = useFetchNlpFlowsQuery({ orgId, tenantId });
  const { data: nlpPipelines, isLoading: isLoadingNlpPipelines } = useFetchNlpPipelinesQuery({ orgId, tenantId });
  const [, { isLoading: isCreatingNlpFlow }] = useCreateNlpFlowMutation({ fixedCacheKey: 'create-nlp-flow-shared' });
  const [deleteNlpFlow] = useDeleteNlpFlowMutation({ fixedCacheKey: 'delete-nlp-flow-shared' });
  const [activePanelKey, setActivePanelKey] = useState(searchParams.has('nlpFlowId') ? PANEL_KEYS.EDIT : PANEL_KEYS.LIST);

  // Local state
  const [searchFilter, setSearchFilter] = useState(''); // The search input value
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);

  const columns = [
    {
      title: t('NLP Flow Name'),
      dataIndex: 'name',
      key: 'name',
      render: (_text, record) => (
        <Highlighter
          highlightClassName="bg-highlight p-0"
          searchWords={[searchFilter]}
          autoEscape
          textToHighlight={(record.name)}
        />
      )
    },
    {
      title: t('NLP Systems Included'),
      dataIndex: 'pipelinesIncluded',
      key: 'pipelinesIncluded',
      render: (_text, record) => {
        if (isLoadingNlpPipelines) {
          return <Spin size="small" />;
        }

        const pipelineIdsInFlow = record.nodes.filter(node => node.type === NODE_TYPES.PIPELINE && node.data?.pipelineId).map(node => node.data?.pipelineId);

        return (
          <Space size={0} split={<span className="mr-1">,</span>}>
            {
              pipelineIdsInFlow.map(pipelineId => {
                // Find the pipelines from the list we fetched
                // Alternatively, we could have provided the pipelineId to the component, but that would mean that we should fetch each pipeline individually
                const pipeline = nlpPipelines?.items.find(pipeline => pipeline.id === pipelineId);
                return (<NlpPipelineLink pipeline={pipeline} showIcon={false} loader={null} />);
              })
            }
          </Space>
        );
      }
    },
    {
      title: t('Language'),
      dataIndex: 'language',
      key: 'language',
      render: (_text, record) => {
        const language = LANGUAGE_OPTIONS.find(language => language.key === record.language);
        return language ? language.label : null;
      }
    },
    {
      title: t('Created at'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      responsive: ['lg'],
      render: (_text, record) => {
        if (record.createdAt) {
          return moment(record.createdAt).format(DEFAULT_DATETIME_FORMAT);
        } else {
          return '-';
        }
      }
    },
    {
      title: t('Updated at'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      responsive: ['xl'],
      render: (_text, record) => {
        if (record.updatedAt) {
          return moment(record.updatedAt).format(DEFAULT_DATETIME_FORMAT);
        } else {
          return '-';
        }
      }
    },
    {
      title: t('Actions'),
      key: 'actions',
      fixed: 'right',
      align: 'center',
      width: 140,
      hidden: !userPermissions.some(permission => PERMISSIONS_GROUP_VIEWER.includes(permission)),
      render: record => {
        if (userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission))) {
          return (
            <Space onClick={(e) => e.stopPropagation()} size="small">
              <Tooltip title={t('Edit Flow')}>
                <Button
                  shape="circle"
                  type="text"
                  icon={<EditIcon />}
                  onClick={() => onNlpFlowSelect(record.id)}
                />
              </Tooltip>

              <Popconfirm
                title={
                  <Fragment>
                    <Text className="fw-bold">
                      {t('Delete Flow?')}<br/>
                    </Text>
                    <Text>
                      {t('The NLP Flow will be permanently deleted')}
                    </Text>
                  </Fragment>
                }
                okText={t('Delete')}
                cancelText={t('Cancel')}
                placement="topRight"
                onConfirm={() => {
                  onDelete(record.id);
                }}
              >
                <Tooltip title={t('Delete Flow')}>
                  <Button
                    shape="circle"
                    type="text"
                    icon={<DeleteIcon />}
                  />
                </Tooltip>
              </Popconfirm>
            </Space>
          );
        }
      }
    }
  ];

  useEffect(() => {
    setActivePanelKey(searchParams.has('nlpFlowId') ? PANEL_KEYS.EDIT : PANEL_KEYS.LIST);
  }, [searchParams]);

  const onBackToListClick = () => {
    searchParams.delete('nlpFlowId');

    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    }, { replace: true });

    setActivePanelKey(PANEL_KEYS.LIST);
  };

  const onDelete = async (nlpFlowId) => {
    await deleteNlpFlow({ orgId, tenantId, nlpFlowId });
  };

  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };
  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const onNlpFlowCreated = async (createdNlpFlow) => {
    closeCreateModal();
    // Navigate to the newly-created flow right away
    onNlpFlowSelect(createdNlpFlow.id);
  };

  const onNlpFlowSelect = (nlpFlowId) => {
    searchParams.set('nlpFlowId', nlpFlowId);

    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    });
  };

  const handleTableChange = (pagination, _filters, _sorter) => {
    setPageSize(pagination.pageSize);
  };

  return (
    <Fragment>
      <Collapse
        accordion
        expandIcon={() => null}
        ghost
        activeKey={activePanelKey}
        className="ant-collapse-p-0"
      >
        <Collapse.Panel key={PANEL_KEYS.LIST}>
          <Fragment>
            <Row gutter={24} wrap={isMobileSize}>
              <Col flex="auto" className="mb-2">
                <Input
                  size="large"
                  style={{ maxWidth: isMobileSize ? '100%' : 280 }}
                  placeholder={t('Search in contents')}
                  allowClear
                  prefix={<SearchOutlined className="text-muted" />}
                  className="w-100"
                  onChange={(e) => {
                    setSearchFilter(e.target.value);
                  }}
                />
              </Col>

              <Col
                flex={isMobileSize ? 'auto' : 'none'}
                className="mb-2"
              >
                <Permissioned
                  allowedPermissions={PERMISSIONS_GROUP_ADMIN}
                  currentPermissions={userPermissions}
                  fallbackComponent={null}
                >
                  <Button
                    icon={<PlusIcon />}
                    type="primary"
                    className="float-right mb-2"
                    size="large"
                    block={isMobileSize}
                    onClick={openCreateModal}
                    loading={isCreatingNlpFlow}
                  >
                    {t('Add NLP Flow')}
                  </Button>
                </Permissioned>
              </Col>
            </Row>

            <Table
              size="small"
              pagination={{
                size: 'default',
                pageSize: pageSize,
                showSizeChanger: true
              }}
              onRow={(record) => ({
                onMouseDown: handleMouseDown,
                onClick: () => {
                  if(!isDragging() && userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission))) {
                    onNlpFlowSelect(record.id);
                  }
                }
              })}
              rowClassName="cursor-pointer"
              dataSource={nlpFlows?.items.filter(flow => flow.name?.toLowerCase().includes(searchFilter.toLowerCase()))}
              columns={columns}
              loading={isLoadingNlpFlows}
              onChange={handleTableChange}
            />
          </Fragment>
        </Collapse.Panel>

        <Collapse.Panel key={PANEL_KEYS.EDIT}>
          <NlpFlowEditor onBackToListClick={onBackToListClick} />
        </Collapse.Panel>
      </Collapse>

      <Modal
        title={t('Create New NLP Flow')}
        open={isCreateModalOpen}
        onCancel={closeCreateModal}
        footer={null}
        style={{ maxWidth: 600 }}
        width={'95%'}
        centered
      >
        <NlpFlowForm onSuccess={onNlpFlowCreated} />
      </Modal>
    </Fragment>
  );
};

export default NlpFlowsTable;
